import _ from "lodash";
import { NOT_EXPIRES_TEXT } from "../components/ExpirationCertificate/constants";

/**
 * Calcula la diferencia en años y meses entre dos fechas.
 * @param {Date} date1 - La primera fecha.
 * @param {Date} date2 - La segunda fecha.
 * @returns {Object} - Un objeto con la diferencia en años y meses.
 */
const getYearMonthDifference = (date1, date2) => {
  let yearsDifference = date1.getFullYear() - date2.getFullYear();
  let monthsDifference = date1.getMonth() - date2.getMonth();

  if (monthsDifference < 0 || (monthsDifference === 0 && date2.getDate() < date1.getDate())) {
    yearsDifference--;
    monthsDifference += 12;
  }

  return {
    years: yearsDifference,
    months: monthsDifference
  };
}

/**
 * Calcula la fecha de expiración y la diferencia en años.
 * @param {string} expiresValue - La fecha de expiración en formato 'YYYY-MM-DD'.
 * @returns {Object} - Objeto con la fecha formateada y la diferencia de años hasta la expiración.
 */
export const calculateCertificateFileExpired = (expiresValue, notExpiresValue) => {
  if (!notExpiresValue && expiresValue) {
    const currentDate = new Date();
    
    const [year, month, day] = expiresValue.split("-");
    const formattedDate = new Date(`${year}-${month}-${day}`);
  
    const { years, months } = getYearMonthDifference(formattedDate, currentDate);
    
    const formattedExpiresDate = `${day}/${month}/${year}`;
  
    const remainingOneHalfYear = (years === 1 && months >= 6) || (years === 0 && months >= 18);
  
    return {
      formattedExpiresDate,
      timeRemainingUntilExpiration: years,
      remainingOneHalfYear,
      notExpiresValue
    };
  }

  return {
    formattedExpiresDate: NOT_EXPIRES_TEXT,
    timeRemainingUntilExpiration: 0,
    remainingOneHalfYear: 0,
    notExpiresValue
  };
}

export const assignLogoToUkcaAndMedProperties = (ruleUkca, ruleMed, ukcaLogo, medLogo) => {
  const ruleUkcaObj = {
    ruleUkca: {
      logo: ukcaLogo,
      title: "ukca",
      code: "8512/2025"
    }
  };

  const ruleMedObj = {
    ruleMed: {
      logo: medLogo,
      title: "med",
      code: "2923/2025"
    }
  };

  const ruleUkcaWithLogo = 
    ruleUkca 
      ? ruleUkcaObj  
      : null
  
  const ruleMedWithLogo = 
    ruleMed 
      ? ruleMedObj  
      : null

  return { 
    ...ruleUkcaWithLogo,
    ...ruleMedWithLogo
  }
}

/**
 * Validate that the given attributes is an array and has at least one element.
 * @param {Object} attributes - The attributes to validate.
 * @returns {boolean} - Whether the attributes is valid or not.
 */
const validateArrayAttributes = (attributes) => {
  return attributes && Array.isArray(attributes) && attributes.length > 0;
}

/**
 * Process the given data and return the final data in the expected format.
 * 
 * @param {Object} data - The data to process.
 * @returns {Object} - The final data in the expected format.
 */
export const processOtherAttributesData = (data) => {
  const EXCLUDED_FIELDS = [
    'temperatureReductionTreatment',
    'embossingSelect',
    'ruleUkca',
    'ruleMed',
    'ukcaLogo',
    'medLogo',
    'certificateDomain',
    'bacterialResistance'
  ]

  const baseData = _.omit(data, EXCLUDED_FIELDS)
  
  const rulesWithLogosAssigned = assignLogoToUkcaAndMedProperties(
    data.ruleUkca,
    data.ruleMed,
    data.ukcaLogo,
    data.medLogo
  )

  const processedData = {
    embossing: _.get(data, 'embossingSelect.name', null),
    bacterialResistance: validateArrayAttributes(data.bacterialResistance)
      ? data.bacterialResistance
      : null,
    ...rulesWithLogosAssigned
  }

  return {
    ...baseData,
    ...processedData
  }
}